import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import AdminLayout from "../../layout/AdminLayout";
import { useAuth } from "../../../context/AuthContext";
import * as model from "../../../models/RequirementsModel";
import { useNotificationCount } from "../../../context/NotificationCountContext";
import RequirementTable from "./RequirementTable";
import { ConfirmDelete } from "../../modals/ConfirmDelete";
import { apiError } from "../../../helpers/ErrorHelper";
import { toast } from "react-toastify";
import PageHeader from "../../common/PageHeader";
import ListFilterActions from "../../common/ListFilterActions";
import config from "../../../config/config";
import { getData as getClients } from '../../../models/ClientsModel';
import { getData as getContacts } from '../../../models/ContactsModel';
import { getData as getSkills } from '../../../models/SkillsModel';
import { getManagers } from '../../../models/UserModel';
import { getRequirementStatus } from '../../../models/MasterModel';
import { MyReactSelect, MyReactSelectMultiple } from "../../inputs/inputs";
import { RequirementView } from "./RequirementView";
import { useDebouncedCallback } from 'use-debounce';
import { checkPermission } from "../../../helpers/GeneralHelper";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { utcToLocal } from "../../../helpers/DateHelper";
import WorkflowHelpBanner from "../../common/WorkflowHelpBanner";

export default function Requirements() {
  const module = {
    title: 'Requirements',
    singularTitle: 'Requirement',
    key: 'requirements',
    route: 'requirements',
    primaryCol: '_requirement_id'
  };

  const { state } = useAuth();
  let user_data = state.user_data;
  const [searchParams] = useSearchParams();
  var _client_id = searchParams.get('_client_id');
  var _contact_id = searchParams.get('_contact_id');
  const navigate = useNavigate();

  const { notificationCount, setNotificationCount } = useNotificationCount();

  // Update Start
  const [singleData, setSingleData] = useState({});
  const openUpdateModal = (data) => {
    navigate(`/requirements/${data.requirement_unique_id}/edit`, { 
      state: {
        data: data,
        module: module,
      },
    });
  };
  // Update End

  // Delete Start
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const openDeleteModal = (data) => {
    setSingleData(data);
    setToggleDeleteModal(true);
  };

  const deleteData = async() => {
    setLoading(true);

    try {
      const response = await model.remove(user_data.user_token, singleData[module.primaryCol]);
      if (response.status) {
        setNotificationCount(localStorage.getItem('unread_notification_count'));
        toast.success(response.message);

        model.saveToStorage(response.data[module.key], response.data.pagination);
        refreshData(); 
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);

      if (error.response?.data.invalid_token == 1) {
        navigate('/re-login');
      }
    }

    setToggleDeleteModal(false);
    setLoading(false);
  }
  // Delete End

  const [toggleViewModal, setToggleViewModal] = useState(false);
  const handleView = (data) => {
    //setSingleData(data);
    navigate(`/requirements/${data.requirement_unique_id}/view`, { 
      state: {
        data: data,
        module: module,
      },
    });
    setToggleViewModal(true);
  }

  // Flush Start
  const flashData = async () => {
    localStorage.removeItem(module.key);
    fetchData(limit, page);
  };
  // Flush End

  // Filter Start
  const [filters, setFilters] = useState({
    'requirement_client_id': '',
    'requirement_contact_id': '',
    'requirement_manager_id': '',
    'requirement_status_id': '',
    'requirement_skill_id': [],
    requirement_date_from: '',
    requirement_date_to: '',
  });

  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [managers, setManagers] = useState([]);
  const [reqStatus, setReqStatus] = useState([]);
  const [skills, setSkills] = useState([]);
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const result = await getClients({user_token: user_data.user_token, page: 1, limit: -1});

        const clientOptions = result.clients.map(client => ({
          value: client._client_id,
          label: client.client_name
        }));

        setClients(clientOptions);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchManagers = async() => {
      try {
        let apiParam = {user_token: user_data.user_token};
        const response = await getManagers(apiParam);

        if (typeof response != typeof undefined) {
          const options = response.managers.map(ele => ({
            value: ele._user_id,
            label: ele.user_first_name + ' ' + ele.user_last_name
          }));

          setManagers(options);
        }
      } catch (error) {
          console.error(error);
      }
    }

    const fetchStatus = async() => {
      try {
        let apiParam = {user_token: user_data.user_token};
        const response = await getRequirementStatus(apiParam);

        if (typeof response != typeof undefined) {
          const options = response.requirements_master.map(ele => ({
            value: ele._status_id,
            label: ele.status_name
          }));

          setReqStatus(options);
        }
      } catch (error) {
          console.error(error);
      }
    }

    const fetchSkills = async() => {
      try {
        let apiParam = {user_token: user_data.user_token, page: 1, limit: -1};
        const response = await getSkills(apiParam);

        if (typeof response != typeof undefined) {
          const options = response.skills.map(ele => ({
            value: ele._skill_id,
            label: ele.skill_name
          }));

          setSkills(options);
        }
      } catch (error) {
        console.error(error);
      }
    }

    fetchClients();
    fetchManagers();
    fetchStatus();
    fetchSkills();
  }, []);

  const [loadingContacts, setLoadingContacts] = useState(false);
  useEffect(() => {
    const fetchContacts = async() => {
      if(filters.requirement_client_id){
        _client_id = filters.requirement_client_id.value;
      }
      if (!_client_id) {
        filters.requirement_contact_id = '';
        setContacts([]);
        return;
      }

      setLoadingContacts(true);

      try {
        let apiParam = {user_token: user_data.user_token, 'contact_client_id': _client_id, page: 1, limit: -1};
        const response = await getContacts(apiParam);

        if (typeof response != typeof undefined) {
          let options = [];
          let flag = false;
          response.client_contacts.forEach(function(item) {
            if (filters.requirement_contact_id == item._contact_id || filters.requirement_client_id?.value == item._contact_id) {
              flag = true;
            }
            options.push({
              value: item._contact_id,
              label: item.contact_name
            });
          });

          if (! flag) {
            filters.requirement_contact_id = '';
          }

          setContacts(options);
        }
      } catch (error) {
        console.error(error);
      }

      setLoadingContacts(false);
    }

    fetchContacts();
  }, [filters.requirement_client_id]);

  const [isFilterReset, setIsFilterReset] = useState(false);
  const [isTrash, setIsTrash] = useState(false);
  useEffect(() => {
    setIsFilterReset(false);
    Object.keys(filters).forEach(item => {
      let isArray = Array.isArray(filters[item]);
      if ((isArray && filters[item].length > 0) || (! isArray && filters[item])) {
        setIsFilterReset(true);
        return false;
      }
    });

    fetchData(10, 1);
  }, [filters, isTrash]);

  const resetFilter = () => {
    let blankFilter = {};
    Object.keys(filters).forEach(item => {
      blankFilter[item] = item === 'requirement_skill_id' ? [] : '';
    });
    setFilters(blankFilter);
  }
  // Filter End

  // List Start
  const [listData, setListData] = useState([]);
  const [cacheTime, setCacheTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTrash = (isTrash) => {
    setIsTrash(isTrash);
  }

  const debounced = useDebouncedCallback(() => fetchData(limit, page), 500);

  const fetchData = async (newLimit, page) => {
    setLoading(true);

    let filterSkills = [];
    for (let skill of filters?.requirement_skill_id) {
      filterSkills.push(skill?.value);
    }

    if(filters.requirement_client_id){
      _client_id = filters.requirement_client_id.value;
     //console.log(filters.contact_client_id);
    }
    if(filters.requirement_contact_id){
      _contact_id = filters.requirement_contact_id.value;
     //console.log(filters.contact_client_id);
    }

    try {
      let apiParam = {
        search: searchQuery,
        page: page,
        limit: newLimit,
        user_token: user_data.user_token,
        requirement_client_id: _client_id ? _client_id :'',
        requirement_contact_id: _contact_id ? _contact_id : '',
        requirement_manager_id: filters?.requirement_manager_id?.value ?? '',
        requirement_status_id: filters?.requirement_status_id?.value ?? '',
        requirement_skill_id: filterSkills.join(','),
        isTrash: isTrash ? 1 : 0,
        date_from: filters.requirement_date_from || '',
        date_to: filters.requirement_date_to || '',
      };
      const response = await model.getData(apiParam);
      if (typeof response == typeof undefined) {
        navigate("/re-login");
        return;
      }

      setListData(response[module.key]);
      setTotalRows(response.pagination.total_rows);
      setLimit(newLimit);
      setPage(page);

      setNotificationCount(localStorage.getItem("unread_notification_count"));

      if (response.time) {
        setCacheTime(response.time);
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };
  // List End

  const refreshData = async () => {
    fetchData(limit, page);
  }

  const exportToExcel = async () => {
    if (!listData || listData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    // Generate timestamp (YYYYMMDD_HHMMSS + random 4 digits)
    const timestamp = new Date().toISOString()
                                .replace(/[-:T]/g, "") // remove special chars
                                .split(".")[0];        // remove milliseconds

    const fileName = `${module.title}_${timestamp}.xlsx`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`${module.title}`);

    // Define headers and style
    const headers = [
      { header: "Requirement", key: "requirement_name" },
      { header: "Client", key: "client_name" },
      { header: "POC", key: "contact_name" },
      { header: "Manager", key: "manager_names" },
      { header: "Type", key: "requirement_type" },
      { header: "Status", key: "status_name" },
      //{ header: "Remarks", key: "requirement_remarks" },
      { header: "Added On", key: "requirement_create_time" },
    ];

    //worksheet.columns = headers;
    worksheet.columns = [
      { header: "Requirement", key: "requirement_name" },
      { header: "Client", key: "client_name" },
      { header: "POC", key: "contact_name" },
      { header: "Manager", key: "manager_names" },
      { header: "Type", key: "requirement_type" },
      { header: "Status", key: "status_name" },
      //{ header: "Remarks", key: "requirement_remarks" },
      { header: "Added On", key: "requirement_create_time" },
    ];

    // Add rows
    listData.forEach(row => {
      worksheet.addRow({
        client_name: row.client_name,
        requirement_name: row.requirement_name || "N/A",
        contact_name: row.contact_name || "N/A",
        manager_names: row.manager_names || "N/A",
        requirement_type: row.requirement_type==='c2h' ? 'Contract to Hire': 'Fulltime Hiring' || "N/A",
        status_name: row.status_name || "N/A",
        //requirement_remarks: row.requirement_remarks || "N/A",
        requirement_create_time: row.requirement_create_time ? utcToLocal(row.requirement_create_time) : "N/A",
      });
    });

    // Style header
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D9E1F2' }, // Light blue
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Generate Excel file and save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, fileName);
  };

  return (
    <AdminLayout>
      <Helmet>
        <title>{module.title} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid cms-list-shell">
          <PageHeader module={module} isTrash={isTrash} toggleTrash={fetchTrash} />

          {!isTrash && <WorkflowHelpBanner stage="requirements" compact />}

          {/* <!-- Start::row-1 --> */}
          <div className="row">
            <div className="col-xl-12">
              <div className="card custom-card cms-list-card">
                {! isTrash && <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="card-title fw-normal" style={{textTransform: 'unset'}}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <MyReactSelect column="requirement_client_id" options={clients} firstOption="Select client" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <MyReactSelect column="requirement_contact_id" options={contacts} firstOption="Select POC" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <MyReactSelect column="requirement_manager_id" options={managers} firstOption="Select Manager" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <MyReactSelect column="requirement_status_id" options={reqStatus} firstOption="Select Status" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <MyReactSelectMultiple column="requirement_skill_id" options={skills} firstOption="Select Skills" fields={filters} setFields={setFilters} /> &nbsp;&nbsp;
                      <span className="text-muted small me-1 align-self-center">Added</span>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        style={{ width: '9.5rem' }}
                        title="Created from"
                        value={filters.requirement_date_from}
                        onChange={(e) => setFilters({ ...filters, requirement_date_from: e.target.value })}
                      />
                      <span className="text-muted small mx-1 align-self-center">—</span>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        style={{ width: '9.5rem' }}
                        title="Created to"
                        value={filters.requirement_date_to}
                        min={filters.requirement_date_from || undefined}
                        onChange={(e) => setFilters({ ...filters, requirement_date_to: e.target.value })}
                      />
                      &nbsp;&nbsp;
                    <ListFilterActions
                        showExport={listData.length > 0}
                        onExport={exportToExcel}
                        showReset={isFilterReset}
                        onReset={resetFilter}
                      />

                      {/* { config.isCache ? `Last Synced On ${cacheTime}` : module.title } */}
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <div>
                      <input type="text" className="form-control table-search" onChange={(e) => {debounced(e.target.value); setSearchQuery(e.target.value);}} placeholder="Search..." autoComplete='off' />

                      {checkPermission(module.route, 'add') && 
                        <button className="btn btn-primary btn-sm" onClick={() => navigate(`/requirements/create`, { state: { module: module }})}>
                          <i className="ri-add-line me-1 fw-semibold align-middle"></i>
                          Add
                        </button>
                      }
                    </div>

                    {config.isCache ? 
                      <button className="btn btn-success-light btn-sm" onClick={() => flashData()}>
                        <i className="ri-refresh-line me-1 fw-semibold align-middle"></i> Refresh
                      </button> : ''
                    }
                  </div>
                </div>}
                <div className="card-body">
                  <div className="table-responsive">
                    <RequirementTable
                      module={module}
                      data={listData}
                      loading={loading}
                      totalRows={totalRows}
                      page={page}
                      limit={limit}
                      fetchData={fetchData}
                      openUpdateModal={openUpdateModal}
                      openDeleteModal={openDeleteModal}
                      handleView={handleView}
                      isTrash={isTrash}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!--End::row-1 --> */}

          {/* <!--Delete Modal Start--> */}
          <ConfirmDelete
            module={module}
            showModal={toggleDeleteModal}
            setShowModal={setToggleDeleteModal}
            loading={loading}
            onConfirm={deleteData}
          />
          {/* <!--Delete Modal End--> */}

          {/* View Modal */}
          <RequirementView
            module={module}
            showModal={toggleViewModal}
            setShowModal={setToggleViewModal}
            data={singleData}
          />
        </div>
      </div>

      <Helmet></Helmet>
    </AdminLayout>
  );
}
